<template>
  <div v-if="shouldShowBanner" class="banner-container">
    <!-- 动态轮播图模式 -->
    <template v-if="advertCode">
      <van-swipe class="swipe" :autoplay="3000" indicator-color="#ff4444">
        <van-swipe-item v-for="banner in bannerList" :key="banner.id">
          <a :href="banner.url" class="banner-link">
            <img
              :src="banner.imageUrl"
              :alt="banner.title"
              class="banner-image"
            />
          </a>
        </van-swipe-item>
      </van-swipe>
    </template>

    <!-- 静态图片模式 -->
    <template v-else-if="staticImage">
      <div class="static-wrapper">
        <img
          :src="staticImage"
          class="static-banner"
          @load="handleImageLoad"
          @error="handleImageError"
          alt="banner图片"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import { getBannerList } from "@/api/advertisement";
import { Swipe, SwipeItem } from "vant";

export default {
  name: "Banner",

  components: {
    [Swipe.name]: Swipe,
    [SwipeItem.name]: SwipeItem,
  },

  props: {
    advertCode: {
      type: String,
      default: "",
    },
    staticImage: {
      type: String,
      default: "",
    },
    pageSize: {
      type: Number,
      default: 5,
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["load-success", "load-error", "data-loaded"],

  setup(props, { emit }) {
    const bannerList = ref([]);
    const loading = ref(false);
    const dataLoaded = ref(false);

    // 计算是否应该显示 banner
    const shouldShowBanner = computed(() => {
      if (!dataLoaded.value) return false;
      return props.staticImage || (bannerList.value && bannerList.value.length > 0);
    });

    const fetchBannerData = async () => {
      if (!props.advertCode || loading.value) return;

      loading.value = true;
      try {
        const res = await getBannerList({
          advertCode: props.advertCode,
          pageSize: props.pageSize,
        });

        if (res?.Data && Array.isArray(res.Data)) {
          bannerList.value = res.Data;
          emit("data-loaded", res.Data);
          emit("load-success", res.Data);
        } else {
          console.error("Banner数据格式错误:", res);
          emit("load-error", new Error("数据格式错误"));
        }
      } catch (error) {
        console.error("获取Banner数据失败:", error);
        emit("data-loaded", []);
        emit("load-error", error);
      } finally {
        loading.value = false;
        dataLoaded.value = true;
      }
    };

    watch(
      () => props.advertCode,
      (newVal) => {
        if (newVal) {
          fetchBannerData();
        } else {
          dataLoaded.value = true; // 如果没有 advertCode，直接标记为已加载
        }
      },
      { immediate: true }
    );

    const handleImageLoad = () => {
      emit("load-success", { type: "static" });
    };

    const handleImageError = (error) => {
      emit("load-error", { type: "static", error });
    };

    return {
      bannerList,
      handleImageLoad,
      handleImageError,
      shouldShowBanner,
    };
  },
};
</script>

<style lang="scss" scoped>
.banner-container {
  width: 96%;
  margin: 5px auto;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 430 / 170;

  .swipe {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;

    .banner-link {
      display: block;
      width: 100%;
      height: 100%;
    }

    .banner-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .static-wrapper {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;

    .static-banner {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

:deep(.van-swipe__track) {
  height: 100%;
}

:deep(.van-swipe-item) {
  height: 100%;
}

:deep(.van-swipe__indicators) {
  bottom: 12px;
}
</style>
